import { FEATURE_AVAILABILITY_TYPE_CODE } from "@/v2-common/featureSurface/featureSurface.constants"
import type { DotProps } from "@/v2-backoffice-client/components/dot"
import { FEATURE_TYPE_CODE } from "@/v2-backoffice-client/feature/feature.constants"
import type { LicenseDecisionSubject } from "@/v2-backoffice-client/license/changes/license.changes.decision.types"
import {
  splitPackageNames,
  whoHasPackaged,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"
import type { FeatureContentCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/__types__/license.featureGroup.contentCatalog.query"

/** One row of Feature access > Content: a map layer or base map of the content catalog. */
export type LicenseContentItem =
  FeatureContentCatalogQuery["getFeatureContentCatalog"][number]

export function isLockedContent(item: LicenseContentItem) {
  return (
    item.FeatureAvailabilityTypeCode ===
    FEATURE_AVAILABILITY_TYPE_CODE.lockedWithinPackage
  )
}

/** The packages that carry the item, as a list. */
export function contentPackagesOf(item: LicenseContentItem) {
  return splitPackageNames(item.FeatureGroupNames)
}

/** "Map layer" / "Base map": the class, in the operator's words. */
export function contentKindOf(item: LicenseContentItem) {
  switch (item.FeatureTypeCode) {
    case FEATURE_TYPE_CODE.basemap:
      return "Base map"
    case FEATURE_TYPE_CODE.mapLayer:
      return "Map layer"
    default:
      return item.FeatureTypeName
  }
}

/** Who can use the item right now, in one plain sentence. */
export function whoHasContent(item: LicenseContentItem) {
  return whoHasPackaged(isLockedContent(item), contentPackagesOf(item))
}

/**
 * What locking the item does in the map: the sentence an operator reads
 * before choosing package-only. A base map leaves the gallery, a layer
 * leaves the chooser, its categories and its search, for every customer
 * without a carrying package.
 */
export function contentLockSentenceOf(item: LicenseContentItem) {
  return item.FeatureTypeCode === FEATURE_TYPE_CODE.basemap
    ? `Removes the base map "${item.Name}" from the map's gallery for every customer whose license carries none of the packages that include it.`
    : `Removes the map layer "${item.Name}" from the layer chooser, its categories and its search for every customer whose license carries none of the packages that include it.`
}

/**
 * The decision about an item, in the words the decision itself uses: the
 * Decision column and the panel. Content arrives free and that is its
 * state, so there is no "needs a decision" here.
 */
export const CONTENT_DECISION = {
  everyone: "Free for everyone",
  packages: "Package-only",
} as const

export type LicenseContentDecision =
  (typeof CONTENT_DECISION)[keyof typeof CONTENT_DECISION]

export function contentDecisionOf(
  item: LicenseContentItem,
): LicenseContentDecision {
  return isLockedContent(item)
    ? CONTENT_DECISION.packages
    : CONTENT_DECISION.everyone
}

/** The hue each decision wears, in the column and the panel alike. */
export const CONTENT_DECISION_DOT_VARIANT: Record<
  LicenseContentDecision,
  DotProps["variant"]
> = {
  [CONTENT_DECISION.everyone]: "success-1",
  [CONTENT_DECISION.packages]: "secondary",
}

/**
 * The status pills' buckets: the decision, with Not live set apart first,
 * because whatever is decided nobody sees an unpublished item until the
 * review queue enables it.
 */
export const CONTENT_STATUS = {
  packageOnly: CONTENT_DECISION.packages,
  everyone: CONTENT_DECISION.everyone,
  notLive: "Not live",
} as const

export type LicenseContentStatus =
  (typeof CONTENT_STATUS)[keyof typeof CONTENT_STATUS]

export function contentStatusOf(
  item: LicenseContentItem,
): LicenseContentStatus {
  if (!item.IsEnabled) return CONTENT_STATUS.notLive
  return contentDecisionOf(item)
}

/** The content row as the shared decision control reads it. */
export function contentDecisionSubjectOf(
  item: LicenseContentItem,
): LicenseDecisionSubject {
  const isLocked = isLockedContent(item)
  return {
    code: item.Code,
    name: item.Name,
    isLocked,
    whoHasIt: whoHasContent(item),
    packageNames: contentPackagesOf(item),
    pickedPackageIds: (item.FeatureGroupIds ?? "").split(",").filter(Boolean),
    lockSentence: contentLockSentenceOf(item),
    // Content arrives free and that IS its state: there is no "nobody has
    // said so" to record, so the control always shows one of the two.
    decision: isLocked ? "packages" : "everyone",
    canUndecide: false,
    featureCodeLabel: "WhatsNewContent",
  }
}
